import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const TASK_TYPES = [
  { key: "followup", label: "Follow-up" },
  { key: "call", label: "Chiamata" },
  { key: "no_show_recontact", label: "Richiamo no-show" },
];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export function EditActivityModal({ task, open, onClose, onDone, extraActions }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("followup");
  const [dt, setDt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task && open) {
      setTitle(task.title || "");
      setType(task.type || "followup");
      setDt(toLocalInput(task.due_at));
      setNote(task.note || "");
    }
  }, [task, open]);

  const save = async () => {
    if (!dt) { toast.error("Seleziona data e ora"); return; }
    setSaving(true);
    try {
      await api.put(`/tasks/${task.id}`, {
        title, type, due_at: new Date(dt).toISOString(), note,
      });
      setSaving(false);
      toast.success("Attività aggiornata");
      onClose(); onDone && onDone();
    } catch (e) {
      setSaving(false);
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (!task) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-activity-modal">
        <DialogHeader><DialogTitle className="font-heading">Modifica attività</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Tipo attività</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="edit-activity-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Titolo / descrizione</Label>
            <Input data-testid="edit-activity-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Ricontattare" />
          </div>
          <div className="space-y-1"><Label className="text-xs">Data e ora</Label>
            <Input data-testid="edit-activity-datetime" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
          </div>
          <div className="space-y-1"><Label className="text-xs">Nota</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota…" rows={2} data-testid="edit-activity-note" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {extraActions ? <div className="flex gap-2 mr-auto">{extraActions}</div> : <span />}
          <Button data-testid="edit-activity-save" onClick={save} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditActivityModal;
